import { useGetAppointmentStats } from '@pa/api/clinic'
import { fmtShortDate, Segmented, WEEKDAYS_SHORT, weekdayOf } from '@pa/ui'
import { TrendingUp } from 'lucide-react'
import type { StatsPeriod } from '../model/search'
import { SourceName } from './tables'
import { TrendChart, type TrendPoint } from './TrendChart'

/** localStorage key of the card's hidden state (UserFlows C-01: remembered on the device). */
export const STATS_HIDDEN_KEY = 'pa.appointmentsStatsHidden'

export const PERIOD_LABEL: Record<StatsPeriod, string> = {
  week: 'This week',
  month: 'This month',
  '3m': 'Last 3 months',
}

/** How many sources the card lists before "+N more" (UserFlows C-01). */
export const TOP_SOURCES = 4

/**
 * C-01 Booking stats card (UserFlows C-01, Frontend §6.3), ported from the mockup's `BookingStat`: the period switch,
 * "Appointments booked · {period}" up to now, the top 4 sources with "+N more", and the trend (daily for a week or a
 * month, weekly for 3 months, stopping at today). Counting and bucketing are the server's, in clinic time (#16).
 */
export function BookingStatsCard({
  clinicId,
  period,
  onPeriod,
}: {
  clinicId: string
  period: StatsPeriod
  onPeriod: (p: StatsPeriod) => void
}) {
  const stats = useGetAppointmentStats(clinicId, { period })
  const data = stats.data?.period === period ? stats.data : undefined
  const points: TrendPoint[] = (data?.buckets ?? []).map((b) => ({
    label:
      data?.bucketUnit === 'week'
        ? `Wk of ${fmtShortDate(b.start)}`
        : period === 'week'
          ? (WEEKDAYS_SHORT[weekdayOf(b.start)] ?? '')
          : fmtShortDate(b.start),
    value: b.count,
  }))
  const sources = data?.sources ?? []

  return (
    <section className="card stat-card" aria-labelledby="stats-title" aria-busy={!data}>
      <div className="stat-left">
        <div className="stat-head">
          <h2 className="stat-title" id="stats-title">
            <TrendingUp aria-hidden /> Booking stats
          </h2>
        </div>
        <Segmented
          label="Stats period"
          value={period}
          onChange={onPeriod}
          block
          options={[
            { value: 'week', label: PERIOD_LABEL.week },
            { value: 'month', label: PERIOD_LABEL.month },
            { value: '3m', label: PERIOD_LABEL['3m'] },
          ]}
        />
        <div className="stat-label">Appointments booked · {PERIOD_LABEL[period]}</div>
        <div className="stat-value">{stats.isError ? '—' : (data?.total ?? '…')}</div>
        {stats.isError && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              void stats.refetch()
            }}
          >
            Retry
          </button>
        )}
        <div className="stat-sources">
          {sources.slice(0, TOP_SOURCES).map((s) => (
            <span key={s.source ?? '__unknown__'}>
              <SourceName row={{ source: s.source, sourceSaved: s.saved }} /> <b>{s.count}</b>
            </span>
          ))}
          {sources.length > TOP_SOURCES && <span className="muted">+{sources.length - TOP_SOURCES} more</span>}
        </div>
      </div>
      <div className="stat-chart">
        <TrendChart points={points} />
      </div>
    </section>
  )
}
